import { Form, Formik } from 'formik';
import { useNavigate } from 'react-router-dom';

import { Box } from '@hivebrite/design-system/Box';
import { Button } from '@hivebrite/design-system/Button';
import {
  FontAwesomeIcon,
  CATEGORIES,
} from '@hivebrite/design-system/FontAwesomeIcon';

import { useDefaultAppContexts } from 'hb-react/frontoffice/contexts/withDefaultAppContexts';
import { useGetInitialValuesStep2 } from 'hb-react/shared/apps/Events/components/EventRegistrationContainer/form/initialValues';
import EventRegistrationForm from 'hb-react/shared/apps/Events/components/EventRegistrationForm';
import { RegistrationSteps } from 'hb-react/shared/apps/Events/constants/registrationSteps';
import { useEventCartContext } from 'hb-react/shared/apps/Events/contexts/EventCartContext';
import { useEventContext } from 'hb-react/shared/apps/Events/contexts/EventContext';
import { useGuestsContext } from 'hb-react/shared/apps/Events/contexts/GuestsContext';
import { useRegistrationFlowContext } from 'hb-react/shared/apps/Events/contexts/RegistrationFlowContext';
import { useWithBillingStepWorkflow } from 'hb-react/shared/apps/Events/hooks/useWithBillingStepWorkflow';
import ErrorFocus from 'hb-react/shared/components/formik/components/ErrorFocus';
import { LockScreen } from 'hb-react/shared/components/LockScreen';
import { displayNotif } from 'hb-react/shared/helpers/notifications';

import { useTicketInformationStepSubmit } from './useTicketInformationStepSubmit';
import Navigation from '../Navigation';
import { ReCaptcha } from '../ReCaptcha';
import RegistrationHelp from '../RegistrationHelp';

const buttonNextContent = ({
  isFixed,
  withBillingStepWorkflow,
  isShippable,
}) => {
  if (isFixed) {
    return s__('NetworkEvents|Next');
  }

  if (withBillingStepWorkflow) {
    return s__('NetworkEvents|Proceed to Billing Information');
  }

  if (isShippable) {
    return s__('NetworkEvents|Proceed to Shipping Information');
  }

  return s__('NetworkEvents|Submit');
};

const ArrowLeftIcon = () => (
  <FontAwesomeIcon
    iconName="chevron-left"
    height="16px"
    iconType={CATEGORIES.SOLID}
  />
);

const TicketInformationStep = () => {
  const navigate = useNavigate();
  const { updateRegistrationFlow, setValuesStep2, valuesStep2 } =
    useRegistrationFlowContext();

  const { currentUser } = useDefaultAppContexts();
  const { event } = useEventContext();
  const guestsContext = useGuestsContext();
  const withBillingStepWorkflow = useWithBillingStepWorkflow();
  const { isShippable } = useRegistrationFlowContext();
  const { resetAndCompleteCart, isLoading = false } = useEventCartContext();

  const { onSubmit } = useTicketInformationStepSubmit({
    guestsContext,
    setValuesStep2,
    withBillingStepWorkflow,
    billingStepFlow: () => {
      updateRegistrationFlow({
        currentStep: RegistrationSteps.BILLING_INFORMATION_STEP,
      });
    },
    freeTicketsFlow: async () => {
      try {
        const { firstname, lastname, email } =
          currentUser ||
          valuesStep2.network_events_booking.attendees_attributes[0];

        const receipt = { firstname, lastname, email };

        await resetAndCompleteCart({ values: valuesStep2, receipt });

        navigate('../success');
      } catch (error) {
        displayNotif(
          'error',
          error?.response?.data?.errors ||
            I18n.t('web.networks.events.bookings.booking_error'),
        );
      }
    },
  });

  const initialValues = useGetInitialValuesStep2(event);

  return (
    <>
      {isLoading && <LockScreen />}
      <RegistrationHelp {...event.registration_help} mt={-2} mb={6} />
      <Box display="block" data-testid="event-registration-form-tickets">
        <Formik initialValues={initialValues} onSubmit={onSubmit}>
          {(formik) => {
            const { isSubmitting } = formik;

            return (
              <Form>
                <EventRegistrationForm />
                <EventRegistrationForm type="addons" />
                {!withBillingStepWorkflow && <ReCaptcha />}
                <Navigation>
                  {({ isFixed }) => (
                    <>
                      <Button
                        onClick={() =>
                          updateRegistrationFlow({
                            currentStep:
                              RegistrationSteps.TICKET_SELECTION_STEP,
                          })
                        }
                        variant="secondary"
                        mr={4}
                        icon={!isFixed && ArrowLeftIcon}
                      >
                        {isFixed
                          ? s__('NetworkEvents|Back')
                          : s__('NetworkEvents|Go back to Ticket Selection')}
                      </Button>
                      <Button
                        dataTestId={`ticket-information-step-${
                          isFixed
                            ? 'next'
                            : withBillingStepWorkflow
                            ? 'proceed'
                            : 'submit'
                        }`}
                        type="submit"
                        isLoading={isSubmitting}
                      >
                        {buttonNextContent({
                          isFixed,
                          isShippable,
                          withBillingStepWorkflow,
                        })}
                      </Button>
                    </>
                  )}
                </Navigation>
                <ErrorFocus />
              </Form>
            );
          }}
        </Formik>
      </Box>
    </>
  );
};

export default TicketInformationStep;
